import {useContext, useEffect, useState} from "react";
import {Context as AppContext} from "../../../context/AppContext";
import umAxios from "../../../config/axios";
import {toast} from "react-toastify";
import {CategoryRepository} from "../../../repositories/CategoryRepository";
import {SignInContext} from "../../../context/SignInContext";
import {
    Button, Pagination,
    Stack,
    Table,
    TableBody,
    TextField,
    InputAdornment,
    Typography,
    useMediaQuery,
    useTheme, Box
} from "@mui/material";
import {grey} from "@mui/material/colors";
import {Link} from "react-router-dom";
import {HugeiconsIcon} from "@hugeicons/react";
import {Add01Icon, Search01Icon} from "@hugeicons/core-free-icons";
import ProjectRichTableRow from "./project-rich-table-row";

const rowsPerPage = 5;


const filterPillSx = (isSelected) => ({
    bgcolor: isSelected ? grey[200] : "transparent",
    color: isSelected ? grey[900] : grey[600],
    fontWeight: isSelected ? 600 : 500,
    borderRadius: "20px",
    py: 0.5, px: 2, minWidth: 0,
    textWrap: "nowrap",
    transition: "background-color 0.15s ease",
    "&:hover": {
        bgcolor: isSelected ? grey[300] : "action.hover",
    },
});

export const ProjectRichTableView = () => {
    const {currentUser, isAuth, organizations} = useContext(SignInContext);
    const {state: {projects}} = useContext(AppContext);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    // Pagination state
    const [page, setPage] = useState(0);
    const [search, setSearch] = useState("");
    const [languages, setLanguages] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedOrganization, setSelectedOrganization] = useState(null);

    const isAdmin = currentUser?.roles?.includes("admin");
    const [demoView, setDemoView] = useState(false);
    const firstName = currentUser?.firstName || currentUser?.name || "there";

    useEffect(() => {
        const fetchLanguages = async () => {
            try {
                const response = await umAxios.get('/api/language-support/all');
                setLanguages(response.data);
            } catch (err) {
                toast.error(err.message);
            }
        };

        if (isAuth) {
            fetchLanguages();
            fetchCategories();
        }
    }, [isAuth, organizations]);

    const fetchCategories = async () => {
        const isAdmin = currentUser?.roles?.includes("admin");
        const fetcher = isAdmin ? CategoryRepository.getAll : () => CategoryRepository.getCategoriesByMember(currentUser.username);
        fetcher()
            .then(response => setCategories(response.data))
            .catch(error => console.log(error));
    };

    const sortedProjects = [...(projects || [])]
        .filter(i => {
            if (isAdmin) {
                return demoView ? i?.is_demo === true : i?.is_demo !== true;
            }
            return !i?.is_demo || i?.createdBy === currentUser?.username;
        })
        .filter(i => selectedOrganization === null || i?.settings?.organization === selectedOrganization?.id)
        .sort((a, b) => new Date(b?.updatedAt || b?.createdAt) - new Date(a?.updatedAt || a?.createdAt));

    const favoriteProjects = sortedProjects.filter(project =>
        project?.settings?.favourites?.includes(currentUser?.username)
    );

    const otherProjects = sortedProjects.filter(project =>
        !project?.settings?.favourites?.includes(currentUser?.username)
    );

    const displayProjects = [...favoriteProjects, ...otherProjects];

    const query = search.trim().toLowerCase();
    const searchedProjects = query
        ? displayProjects.filter(p => (p?.name || "").toLowerCase().includes(query))
        : displayProjects;


    return (
        <Stack direction={"column"} gap={2} maxWidth={"lg"} sx={{mx: "auto", width: {xs: "92%", md: "90%"}, py: 3}}>

            <Stack direction={"column"} gap={2} sx={{borderBottom: `1px solid ${grey[200]}`, pb: 2}}>
                <Stack direction={{xs: "column", md: "row"}} gap={2} alignItems={{xs: "stretch", md: "center"}}>
                    <Typography variant={"h5"} component="div"
                                sx={{
                                    flexShrink: 0,
                                    fontFamily: "Inter, sans-serif",
                                    color: "text.primary",
                                }}>
                        <Box component="span" sx={{
                            display: "block",
                            color: "text.secondary",
                            fontSize: {xs: "0.9rem", md: "0.95rem"},
                            fontWeight: 500,
                            lineHeight: 1.2,
                            mb: 0.35,
                        }}>
                            Welcome back {firstName},
                        </Box>
                        <Box component="span" sx={{
                            display: "block",
                            fontSize: {xs: "1.35rem", md: "1.55rem"},
                            fontWeight: 700,
                            letterSpacing: "-0.025em",
                            lineHeight: 1.2,
                        }}>
                            here&apos;s your project space
                        </Box>
                    </Typography>
                    <TextField
                        data-tour="project-search"
                        size={"small"}
                        placeholder={"Search projects..."}
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(0);
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position={"start"}>
                                    <HugeiconsIcon icon={Search01Icon} size={18} color={grey[500]}/>
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            flex: 1,
                            maxWidth: {md: 420},
                            ml: {md: "auto"},
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "20px",
                                bgcolor: grey[100],
                                fontSize: 14,
                                "& fieldset": {border: "none"},
                                "&:hover": {bgcolor: grey[200]},
                                "&.Mui-focused": {bgcolor: "#fff", "& fieldset": {border: `1px solid ${grey[300]}`}},
                            },
                        }}
                    />
                    <Button
                        data-tour="create-project"
                        component={Link} to={"/project/create"}
                        sx={{textWrap: "nowrap", flexShrink: 0}} variant={"primary"}
                        startIcon={<HugeiconsIcon icon={Add01Icon} size={18}/>}
                    >
                        Create Project
                    </Button>
                </Stack>
                <Stack data-tour="project-filters" direction={"row"} gap={1} alignItems={"center"} flexWrap={"wrap"}>
                    <Typography
                        color={grey[500]}
                        fontWeight={600}
                        fontSize={"0.72em"}
                        letterSpacing={"0.06em"}
                        sx={{textTransform: "uppercase", mr: 0.5}}
                    >
                        Filter by
                    </Typography>
                    <Button
                        data-tour="filter-all"
                        onClick={() => {
                            setSelectedOrganization(null);
                            setDemoView(false);
                            setPage(0);
                        }} size={"small"}
                        color={"secondary"}
                        sx={filterPillSx(selectedOrganization === null && !demoView)}
                    >
                        All
                    </Button>
                    {organizations?.map(organization => {
                        const isSelected = organization?.id === selectedOrganization?.id;
                        return (
                            <Button
                                key={organization?.id}
                                onClick={() => {
                                    setSelectedOrganization(organization);
                                    setDemoView(false);
                                    setPage(0);
                                }} size={"small"}
                                color={"secondary"}
                                sx={filterPillSx(isSelected)}
                            >
                                {organization?.name}
                            </Button>
                        )
                    })}
                    {isAdmin && (
                        <Button
                            onClick={() => {
                                setDemoView(prev => !prev);
                                setSelectedOrganization(null);
                                setPage(0);
                            }}
                            size={"small"} color={"secondary"}
                            sx={filterPillSx(demoView)}
                        >
                            Demos
                        </Button>
                    )}
                </Stack>
            </Stack>

            {projects?.length > 0 && searchedProjects?.length === 0 && <Typography variant={"h4"}
                                                                                   sx={{py: 3}}>{query ? "No projects match your search" : "There are not projects for this organization"}</Typography>}


            {isMobile ? (
                <Stack data-tour="projects-list" direction={"column"} gap={2} sx={{mt: 1}}>
                    {searchedProjects
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map(project => (
                            <ProjectRichTableRow
                                key={project?.id}
                                mobile
                                project={project}
                                organizations={organizations}
                                categories={categories}
                                languages={languages}
                            />
                        ))
                    }
                </Stack>
            ) : (
                <Table data-tour="projects-list" border={0} sx={{borderSpacing: '0 16px', borderCollapse: 'separate'}}>
                    <TableBody>
                        {searchedProjects
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map(project => (
                                <ProjectRichTableRow
                                    key={project?.id}
                                    project={project}
                                    organizations={organizations}
                                    categories={categories}
                                    languages={languages}
                                />
                            ))
                        }
                    </TableBody>
                </Table>
            )}

            {searchedProjects?.length > 0 ? <Stack direction="row" justifyContent="center" mt={2}>
                    <Pagination
                        count={Math.ceil(searchedProjects.length / rowsPerPage)}
                        page={page + 1}
                        onChange={(event, value) => setPage(value - 1)}
                        color="primary"
                        shape="rounded"
                        siblingCount={1}
                        boundaryCount={1}
                        showFirstButton
                        showLastButton
                    />
                </Stack>
                : null
            }
        </Stack>
    );
}
